import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Plus, Minus, ShoppingCart, ExternalLink, Store } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface Product {
  id: string;
  title: string;
  description: string;
  handle: string;
  images: { url: string; altText: string }[];
  variants: {
    id: string;
    title: string;
    price: { amount: number; currencyCode: string };
    availableForSale: boolean;
    image?: { url: string; altText: string };
  }[];
}

interface CartItem {
  variantId: string;
  quantity: number;
  product: Product;
  variant: Product['variants'][0];
}

interface Shop {
  id: string;
  shop_name: string;
  shop_description?: string;
  shop_logo_url?: string;
  shopify_domain?: string;
  profiles?: {
    username: string;
    display_name?: string;
    avatar_url?: string;
  };
}

export default function ShopView() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (shopId) {
      fetchShopAndProducts();
    }
  }, [shopId]);

  const fetchShopAndProducts = async () => {
    try {
      // Fetch shop details with proper profile join
      const { data: shopData, error: shopError } = await supabase
        .from('user_shops')
        .select(`
          id,
          user_id,
          shop_name,
          shop_description,
          shop_logo_url,
          shopify_domain
        `)
        .eq('id', shopId)
        .eq('is_public', true)
        .eq('is_active', true)
        .single();

      if (shopError) throw shopError;

      // Get profile separately
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, display_name, avatar_url')
        .eq('user_id', shopData.user_id)
        .single();

      setShop({
        ...shopData,
        profiles: profileData
      });

      // Fetch products from internal shop_products table
      const { data: productsData, error: productsError } = await supabase
        .from('shop_products')
        .select('*')
        .eq('shop_id', shopId)
        .eq('is_available', true)
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;

      // Transform to match the existing Product interface
      const transformedProducts = (productsData || []).map(product => ({
        id: product.id,
        title: product.name,
        description: product.description || '',
        handle: product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        images: product.image_url ? [{ url: product.image_url, altText: product.name }] : [],
        variants: [{
          id: product.id,
          title: 'Standard',
          price: { amount: product.price, currencyCode: product.currency },
          availableForSale: product.is_available && (product.stock_quantity === null || product.stock_quantity > 0),
          image: product.image_url ? { url: product.image_url, altText: product.name } : null
        }]
      }));

      setProducts(transformedProducts);

    } catch (error) {
      console.error('Error fetching shop:', error);
      toast.error('Errore nel caricamento del negozio');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (product: Product, variant: Product['variants'][0]) => {
    // Se è il prodotto speciale "Visita Negozio", apri il link Shopify
    if (product.id === 'visit-shop') {
      if (shop?.shopify_domain) {
        window.open(`https://${shop.shopify_domain}`, '_blank');
        toast.success('Apertura del negozio Shopify...');
      }
      return;
    }

    if (!variant.availableForSale) {
      toast.error('Prodotto non disponibile');
      return;
    }

    setCart(prev => {
      const existingItem = prev.find(item => item.variantId === variant.id);
      
      if (existingItem) {
        return prev.map(item =>
          item.variantId === variant.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prev, { variantId: variant.id, quantity: 1, product, variant }];
      }
    });

    toast.success('Prodotto aggiunto al carrello');
  };

  const updateCartQuantity = (variantId: string, quantity: number) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.variantId !== variantId));
    } else {
      setCart(prev =>
        prev.map(item =>
          item.variantId === variantId
            ? { ...item, quantity }
            : item
        )
      );
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.variant.price.amount * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }

    setCheckoutLoading(true);
    try {
      const lineItems = cart.map(item => ({
        variantId: item.variantId,
        quantity: item.quantity
      }));

      const { data, error } = await supabase.functions.invoke('shopify-checkout', {
        body: { shopId, lineItems }
      });

      if (error) throw error;

      // Redirect to Shopify checkout
      window.open(data.checkout.webUrl, '_blank');
      
      // Clear cart after successful checkout creation
      setCart([]);
      toast.success('Reindirizzamento al checkout...');

    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Errore nella creazione del checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-secondary p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Caricamento negozio...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen gradient-secondary p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <Card className="glass border-white/10">
            <CardContent className="p-8 text-center">
              <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Negozio non trovato</h3>
              <p className="text-muted-foreground mb-4">
                Il negozio richiesto non è disponibile o non esiste
              </p>
              <Button onClick={() => navigate('/app/shop')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna ai Negozi
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-secondary">
      {/* Header */}
      <div className="glass border-b border-primary/20 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/app/shop')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              
              <div className="flex items-center gap-3">
                {shop.shop_logo_url ? (
                  <img
                    src={shop.shop_logo_url}
                    alt={shop.shop_name}
                    className="w-12 h-12 rounded-xl object-contain bg-white/10"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    <Store className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-white">{shop.shop_name}</h1>
                  {shop.shop_description && (
                    <p className="text-muted-foreground">{shop.shop_description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Cart Summary */}
            {cart.length > 0 && (
              <Card className="glass border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-primary" />
                      <span className="text-white font-medium">
                        {getTotalItems()} articoli
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">
                        €{getTotalPrice().toFixed(2)}
                      </p>
                    </div>
                    <Button
                      onClick={handleCheckout}
                      disabled={checkoutLoading}
                      className="bg-primary hover:bg-primary/90"
                    >
                      {checkoutLoading ? 'Caricamento...' : 'Checkout'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Shop Owner Info */}
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-12 h-12">
                <AvatarImage src={shop.profiles?.avatar_url} />
                <AvatarFallback>
                  {shop.profiles?.display_name?.[0] || shop.profiles?.username[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white font-medium">
                  Proprietario: {shop.profiles?.display_name || shop.profiles?.username}
                </p>
                {shop.shopify_domain && (
                  <Badge variant="secondary" className="mt-1">
                    <Store className="w-3 h-3 mr-1" />
                    Shopify Store
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        {products.length === 0 ? (
          <Card className="glass border-white/10">
            <CardContent className="p-8 text-center">
              <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Nessun prodotto disponibile
              </h3>
              <p className="text-muted-foreground">
                Questo negozio non ha ancora prodotti in vendita
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="glass border-white/10 overflow-hidden group hover:border-primary/30 transition-all">
                <CardHeader className="p-0">
                  {product.images.length > 0 ? (
                    <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20">
                      <img
                        src={product.images[0].url}
                        alt={product.images[0].altText || product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <Store className="w-16 h-16 text-primary" />
                    </div>
                  )}
                </CardHeader>

                <CardContent className="p-4 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-primary transition-colors">
                      {product.title}
                    </h3>
                    {product.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {product.description}
                      </p>
                    )}
                  </div>

                  {/* Variants */}
                  <div className="space-y-2">
                    {product.variants.map((variant) => {
                      const cartItem = cart.find(item => item.variantId === variant.id);
                      
                      return (
                        <div key={variant.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                          <div className="flex-1">
                            <p className="text-sm text-white">{variant.title}</p>
                            <p className="text-lg font-bold text-primary">
                              €{variant.price.amount.toFixed(2)}
                            </p>
                          </div>
                          
                          {cartItem ? (
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateCartQuantity(variant.id, cartItem.quantity - 1)}
                                className="w-8 h-8 p-0"
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-white font-medium w-8 text-center">
                                {cartItem.quantity}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateCartQuantity(variant.id, cartItem.quantity + 1)}
                                className="w-8 h-8 p-0"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                           ) : (
                            <Button
                              size="sm"
                              onClick={() => addToCart(product, variant)}
                              disabled={!variant.availableForSale}
                              className="bg-primary hover:bg-primary/90"
                            >
                              {product.id === 'visit-shop' ? (
                                <>
                                  <ExternalLink className="w-4 h-4 mr-1" />
                                  Visita
                                </>
                              ) : (
                                <>
                                  <Plus className="w-4 h-4 mr-1" />
                                  Aggiungi
                                </>
                              )}
                            </Button>
                           )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}